import { Injectable } from '@angular/core';
import * as moment from 'moment';

/**
 * QuoteContext describes everything we managed to extract from an inbox
 * message (or call-log transcript) so that the tape chart and the quote
 * builder can be pre-populated with minimum clicks.
 *
 * It is populated by QuoteContextService.parseEmail() and then flows
 * through:  inbox  ->  tape-chart-wiki-list  ->  quote
 */
export interface QuoteContext {
  // Hard dates — set when the guest gave a specific check-in / check-out
  checkInDate:  Date | null;
  checkOutDate: Date | null;

  // Flexible mode — set when the guest wrote "any 5 nights in August"
  // or "sometime between Aug 1 and Aug 31".  In this mode we look for
  // any window of `flexNumNights` inside [flexRangeStart, flexRangeEnd].
  flexMode:       boolean;
  flexNumNights:  number | null;
  flexRangeStart: Date   | null;
  flexRangeEnd:   Date   | null;

  // What the guest wants
  resortCode:      string | null;   // e.g. HK, BR, KMD — matches development code
  resortLabel:     string | null;   // what we show on the chip ("Honua Kai")
  resortKeywords:  string[];        // all matchable forms (for client-side row filter)
  propertyCode:    string | null;   // if they named a specific unit e.g. HK-801
  locationLabel:   string | null;   // broader island/area if no resort detected

  // Unit requirements
  bedrooms:    number | null;       // "2-bedroom" / "two bedroom"  → 2

  // Guest party
  numAdults:   number;
  numChildren: number;

  // Guest identity (from email headers + body)
  guestName:   string | null;
  guestEmail:  string | null;
  guestPhone:  string | null;

  // Quality of the parse — for UI confidence cues
  confidence:  'high' | 'medium' | 'low';
  detected:    string[];             // human-readable list of what was found
  rawSummary:  string;               // one-line summary, displayed in the banner

  /**
   * Written by QuoteComponent whenever it successfully finds or creates a
   * Guest Master Profile for this session's guest. Because all Quote dialogs
   * opened from the same tape-chart context share the SAME QuoteContext object
   * reference, dialog 2 can read a GMP ID that dialog 1 already resolved —
   * preventing duplicate profile creation when multiple date-range dialogs open.
   */
  resolvedGmpId?: number | null;
}

/** One result when running the flex-date finder across tape chart rows. */
export interface AvailableWindow {
  propertyCode: string;
  shortTermRentalId: any;
  addressDevelopmentName: string;
  bedrooms:  number | null;
  bathrooms: number | null;
  viewType:  string | null;
  startDate: Date;
  endDate: Date;
  numNights: number;

  /**
   * Gap-fill score: how well does this window fit between existing bookings?
   * 2 = perfect gap (nights before AND after the window are already booked)
   * 1 = one side adjacent to a booking
   * 0 = open-ended (no adjacent bookings on either side)
   *
   * From a sales perspective, score-2 windows are the most valuable to sell:
   * the property has those exact nights available with no "wasted" shoulder
   * nights, so selling them maximises revenue for the owner.
   */
  gapScore: number;

  /**
   * Per-night price shown on the card. This is the *first-night* yield price,
   * which matches what the Quote dialog's "per night" line uses (quote.component.ts
   * line ~260: `yieldPrice = rate.yieldPriceAMT ? rate.yieldPriceAMT : rate.rateYieldPriceAMT`).
   */
  perNightFromPrice: number | null;

  /**
   * Pre-tax subtotal — sum of each night's yieldPriceAMT across the whole window.
   * The Quote dialog later calls quoteService.findQuoteTotal() which adds taxes,
   * cleaning fees, and discounts, producing a different (larger) grandTotalWithTax.
   * That's why this is clearly labelled "Est. pre-tax" on the card.
   */
  preTaxSubtotal: number | null;

  /**
   * Authoritative totals returned by the bulk-totals endpoint (same rate-quote
   * pipeline Build Quote uses). Populated asynchronously after the card first
   * renders — until then the card falls back to the client-side tape-chart
   * estimate above. Once these land, the card swaps to the exact number the
   * reservationist will see in the Quote dialog — no more surprise drift.
   */
  dbGrandTotal:        number | null;
  dbGrandTotalWithTax: number | null;
  dbPerNightAvg:       number | null;
  pricingStatus:       'pending' | 'ok' | 'error' | 'estimate';
  pricingError:        string | null;

  /**
   * How many consecutive unbooked nights sit between the previous
   * reservation's checkout and our check-in on this property.
   * 0 = our window starts right after a booking ends (no wasted nights before).
   * Computed client-side from tape-chart data — zero extra DB calls.
   */
  gapNightsBefore: number;

  /**
   * How many consecutive unbooked nights sit between our checkout and the
   * next reservation's check-in on this property.
   * 0 = next booking starts right after our checkout (no wasted nights after).
   */
  gapNightsAfter: number;

  /**
   * True when the gap walk hit the edge of the fetched rates[] window without
   * encountering a reservation — i.e. the real gap is AT LEAST `gapNightsBefore`
   * but we can't see past the backend's ±10-day fetch pad (TapeChartBL.java L116).
   * UI renders `Xn+` instead of `Xn` when truthy so agents know the value is a
   * lower bound, not a ceiling.
   */
  gapBeforeTruncated: boolean;
  gapAfterTruncated:  boolean;

  /**
   * Post-coupon per-night rate from the bulk-totals engine response.
   * = (nightlyLine.finalPrice × numNights + couponLine.finalPrice) / numNights
   * Null until bulk-totals responds. Use this for the "List Price" display
   * in the finder card instead of perNightFromPrice (tape-chart raw daily).
   */
  dbPostCouponNightlyRate: number | null;

  /** Thumbnail URL fetched asynchronously after the finder runs. Null until loaded. */
  thumbImg: string | null;

  /**
   * DB-sourced fit score returned by the bulk-totals endpoint.
   * 2 = Best Fit, 1 = Good Fit, 0 = Available.
   * Null until the bulk-totals response lands — UI falls back to gapScore meanwhile.
   * When populated, this score wins over gapScore for badges and sorting because it
   * uses the full YieldMax algorithm signals (gap discount %, occupancy deviation %, yield adj %).
   */
  fitScore:       number | null;

  /** Short reason code: GAP_FILL | CONNECTING | PEAK_DEMAND | BELOW_PACE | LOW_RATE | SHORT_STAY | AVAILABLE */
  fitReason:      string | null;

  /** Human-readable label ready for display: "fills a gap", "extends a stay", etc. */
  fitReasonLabel: string | null;
}

@Injectable({ providedIn: 'root' })
export class QuoteContextService {

  /** Known KBM developments / resorts — used to recognise what the guest named. */
  private readonly RESORTS: Array<{ code: string; label: string; keywords: string[] }> = [
    { code: 'HK',  label: 'Honua Kai',             keywords: ['honua kai', 'honuakai', 'honua-kai', 'hk']            },
    { code: 'BR',  label: 'Black Rock',            keywords: ['black rock', 'blackrock', 'black-rock', 'br']         },
    { code: 'KMD', label: 'Kamaole Sands',         keywords: ['kamaole', 'kamaole sands', 'kam sands', 'kmd']        },
    { code: 'WKN', label: 'Wailea Kai',            keywords: ['wailea kai', 'wailea-kai', 'wkn']                     },
    { code: 'KBR', label: 'Kaanapali Beach Royale',keywords: ['kaanapali beach royale', 'kbr']                       },
    { code: 'KVR', label: 'Kaanapali Villa Resort',keywords: ['kaanapali villa', 'kvr']                              },
    { code: 'NP',  label: 'Napili Point',          keywords: ['napili point', 'napili-point', 'np']                  },
    { code: 'NK',  label: 'Napili Kai',            keywords: ['napili kai', 'napili-kai', 'nk']                      },
    { code: 'PP',  label: 'Papakea',               keywords: ['papakea', 'papa kea', 'pp']                           },
    { code: 'MS',  label: 'Maui Sunset',           keywords: ['maui sunset', 'ms']                                   },
    { code: 'KB',  label: 'Kahana Beach',          keywords: ['kahana beach', 'kahana-beach', 'kb']                  },
    { code: 'WB',  label: 'Whalers Bay',           keywords: ['whalers bay', 'whaler\'s bay', 'wb']                  },
  ];

  /** Broad location keywords when the guest names an area instead of a resort. */
  private readonly LOCATIONS: string[] = [
    'maui', 'kaanapali', 'kihei', 'wailea', 'napili', 'kahana', 'lahaina',
    'kauai', 'big island', 'oahu', 'west maui', 'south maui'
  ];

  /** Word-form numbers → digits, so "four nights" is recognised as "4 nights". */
  private readonly WORD_NUMBERS: Record<string, number> = {
    one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
    eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13,
    fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18,
    nineteen: 19, twenty: 20, thirty: 30, 'a couple': 2, 'couple of': 2, 'a few': 3
  };

  /* ------------------------------------------------------------------ */
  /*  PUBLIC API                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Main entry point. Returns an empty / low-confidence context when the
   * inputs are empty rather than throwing, so callers can always trust the
   * returned object to be non-null.
   */
  parseEmail(opts: {
    text?:        string;
    subject?:     string;
    senderName?:  string;
    senderEmail?: string;
  }): QuoteContext {

    const raw = this.stripHtml((opts.text ?? '') + ' ' + (opts.subject ?? ''));
    // Normalised text: lowercase + word numbers → digits so every regex below works.
    const body  = this.normaliseWordNumbers(raw);
    const lower = body.toLowerCase();

    const ctx: QuoteContext = this.emptyContext();
    ctx.guestName  = opts.senderName  ?? null;
    ctx.guestEmail = opts.senderEmail ?? null;

    // --- guest phone  ----------------------------------------------
    const phone = raw.match(/(\+?\d[\d\s().-]{8,}\d)/);
    if (phone) {
      ctx.guestPhone = phone[1].trim();
      ctx.detected.push('phone');
    }

    // --- party size  -----------------------------------------------
    // "2 adults", "2 guests", "couple of adults" (now normalised to "2 adults")
    const adultsMatch   = lower.match(/(\d+)\s*(adults?|grown[-\s]?ups?|guests?)/);
    const childrenMatch = lower.match(/(\d+)\s*(children|kids|child|infants?|toddlers?)/);
    const familyMatch   = lower.match(/family\s*of\s*(\d+)/);

    if (adultsMatch)   { ctx.numAdults   = +adultsMatch[1];   ctx.detected.push(`${ctx.numAdults} adults`); }
    if (childrenMatch) { ctx.numChildren = +childrenMatch[1]; ctx.detected.push(`${ctx.numChildren} children`); }
    if (familyMatch && !adultsMatch) {
      const total = +familyMatch[1];
      ctx.numAdults   = Math.max(2, total - ctx.numChildren);
      ctx.detected.push(`family of ${total}`);
    }

    // --- bedrooms ("2-bedroom", "two bedroom", "2 br", "2bd", "2 bed") ---
    // Handles hyphenated and spaced forms. Word numbers already normalised.
    const bedMatch = lower.match(
      /(\d+)\s*(?:-|\s)?\s*(?:bedrooms?|bdrms?|br|bd|bed)\b/
    );
    if (bedMatch) {
      ctx.bedrooms = +bedMatch[1];
      ctx.detected.push(`${ctx.bedrooms}-bedroom`);
    }

    // --- resort / property / location  -----------------------------
    for (const r of this.RESORTS) {
      if (r.keywords.some(k => lower.includes(k))) {
        ctx.resortCode     = r.code;
        ctx.resortLabel    = r.label;
        ctx.resortKeywords = [...r.keywords, r.label.toLowerCase(), r.code.toLowerCase()];
        ctx.detected.push(r.label);
        break;
      }
    }
    // property code (e.g. HK-801, BR 320, KMD-215) — only on the original text
    // so we keep the ORIGINAL casing check.
    const propMatch = raw.match(/\b([A-Z]{2,4})[\s-]?(\d{2,4})\b/);
    if (propMatch) {
      ctx.propertyCode = `${propMatch[1]}-${propMatch[2]}`;
      ctx.detected.push(`unit ${ctx.propertyCode}`);
    }
    if (!ctx.resortCode) {
      for (const loc of this.LOCATIONS) {
        if (lower.includes(loc)) { ctx.locationLabel = this.titleCase(loc); ctx.detected.push(this.titleCase(loc)); break; }
      }
    }

    // --- dates  -----------------------------------------------------
    this.parseDates(lower, ctx);

    // --- confidence  ------------------------------------------------
    const hasDates  = !!(ctx.checkInDate && ctx.checkOutDate) || ctx.flexMode;
    const hasResort = !!(ctx.resortCode || ctx.locationLabel || ctx.propertyCode);
    const hasBeds   = !!ctx.bedrooms;

    if (hasDates && hasResort)            ctx.confidence = 'high';
    else if (hasDates || hasResort || hasBeds) ctx.confidence = 'medium';
    else                                  ctx.confidence = 'low';

    ctx.rawSummary = this.buildSummary(ctx);
    return ctx;
  }

  /**
   * Replaces English word numbers with digits, case-insensitive.
   * Must run BEFORE regex matching so all numeric patterns see digits.
   * "four nights in august"  → "4 nights in august"
   * "a couple of adults"     → "2 adults"
   * "two-bedroom"            → "2-bedroom"
   */
  private normaliseWordNumbers(s: string): string {
    let out = (s || '').toLowerCase();

    // multi-word phrases first (order matters — longer phrases before shorter)
    out = out.replace(/\ba couple of\b/g, '2')
             .replace(/\bcouple of\b/g,    '2')
             .replace(/\ba few\b/g,        '3');

    // single words — bounded so we don't replace "fourteen" when we target "four"
    Object.entries(this.WORD_NUMBERS).forEach(([word, num]) => {
      if (word.includes(' ')) return; // already handled above
      const re = new RegExp(`\\b${word}\\b`, 'g');
      out = out.replace(re, String(num));
    });

    return out;
  }

  /**
   * Given the tapeChartsByProperty rows (already grouped by property), find
   * every window of `numNights` where ALL nights are available for at least
   * one selected/filtered property. Optionally filter by resort code.
   *
   * This is the engine behind "any 5 days in August at Honuakai".
   */
  findAvailableWindows(
    tapeChartsByProperty: any[],
    numNights: number,
    rangeStart: Date,
    rangeEnd: Date,
    resortCode?: string,
    resortKeywords?: string[],
    bedrooms?: number | null
  ): AvailableWindow[] {
    const windows: AvailableWindow[] = [];
    if (!tapeChartsByProperty || !numNights || numNights < 1) return windows;

    const rangeStartMs = new Date(rangeStart).setHours(0, 0, 0, 0);
    const rangeEndMs   = new Date(rangeEnd)  .setHours(0, 0, 0, 0);

    for (const property of tapeChartsByProperty) {
      // Resort filter — accepts development code, keyword match, or prop-code prefix
      if (!this.matchesResort(property, resortCode, resortKeywords)) continue;
      // Bedroom filter
      if (bedrooms != null) {
        const b = Number(property.bedrooms);
        if (!isNaN(b) && b !== bedrooms) continue;
      }

      const rates = (property.rates || []).slice()
        .sort((a, b) => new Date(a.analyzedDate).getTime() - new Date(b.analyzedDate).getTime());

      // scan with sliding window of `numNights`
      for (let i = 0; i + numNights <= rates.length; i++) {
        const slice = rates.slice(i, i + numNights);
        const firstDate = new Date(slice[0].analyzedDate).setHours(0, 0, 0, 0);
        const lastDate  = new Date(slice[slice.length - 1].analyzedDate).setHours(0, 0, 0, 0);

        // inside requested range?
        if (firstDate < rangeStartMs || lastDate > rangeEndMs) continue;

        // must be consecutive days (no gaps) AND fully available
        let ok = true;
        let priceFirst: number | null = null;
        let preTaxSum  = 0;                  // sum of every night's yield price
        let anyPrice   = false;
        for (let j = 0; j < slice.length; j++) {
          if (slice[j].reservationId && slice[j].reservationId > 0) { ok = false; break; }
          const expected = new Date(firstDate);
          expected.setDate(expected.getDate() + j);
          if (new Date(slice[j].analyzedDate).setHours(0, 0, 0, 0) !== expected.setHours(0, 0, 0, 0)) { ok = false; break; }
          const nightPrice = slice[j].yieldPriceAMT ?? slice[j].rateYieldPriceAMT ?? null;
          if (j === 0) priceFirst = nightPrice;
          if (nightPrice != null) { preTaxSum += nightPrice; anyPrice = true; }
        }
        if (!ok) continue;

        // Gap-fill score: does the window sit between two bookings?
        // Check the rate row just before the window starts (rates[i-1])
        // and the row on the checkout day (rates[i+numNights]).
        // A "perfect gap" (score 2) means the property NEEDS to sell
        // exactly these nights — they are sandwiched between bookings
        // and will otherwise sit empty. Surfacing them first is the
        // "sell more" logic: it maximises revenue for the owner and
        // gives the guest an available-exactly unit, not one where
        // they displace potential longer stays.
        const prevRate = rates[i - 1];
        const nextRate = rates[i + numNights];
        const prevBooked = !!prevRate
          && !!prevRate.reservationId
          && prevRate.reservationId > 0;
        const nextBooked = !!nextRate
          && !!nextRate.reservationId
          && nextRate.reservationId > 0;
        const gapScore = (prevBooked ? 1 : 0) + (nextBooked ? 1 : 0);

        // Count consecutive unbooked nights immediately BEFORE this window.
        // Stop at the first booked night or the start of the rate array.
        let gapNightsBefore = 0;
        for (let k = i - 1; k >= 0; k--) {
          const r = rates[k];
          if (r.reservationId && r.reservationId > 0) break;
          gapNightsBefore++;
        }
        // If the walk ran all the way to index 0 without hitting a booking,
        // we've exhausted the fetched rates[] pad — the true gap before is
        // AT LEAST this number, could be much larger. Flag so UI can show `Xn+`.
        const gapBeforeTruncated = gapNightsBefore === i;

        // Count consecutive unbooked nights immediately AFTER this window.
        // Stop at the first booked night or the end of the rate array.
        let gapNightsAfter = 0;
        for (let k = i + numNights; k < rates.length; k++) {
          const r = rates[k];
          if (r.reservationId && r.reservationId > 0) break;
          gapNightsAfter++;
        }
        // Same truncation check on the trailing edge. Backend hard-pads ±10
        // days (TapeChartBL.java L116) so the visible tail caps at ~11 rows;
        // we surface that uncertainty rather than silently understating.
        const gapAfterTruncated = i + numNights + gapNightsAfter === rates.length;

        const startDate = new Date(firstDate);
        const endDate   = new Date(firstDate);
        endDate.setDate(endDate.getDate() + numNights);   // checkout = start + N

        windows.push({
          propertyCode: property.propertyCode,
          shortTermRentalId: property?.shortTermRental?.shortTermRentalId
                         ?? property?.rates?.[0]?.shortTermRentalId,
          addressDevelopmentName: property.addressDevelopmentName || '',
          bedrooms:  property.bedrooms  != null ? Number(property.bedrooms)  : null,
          bathrooms: property.bathrooms != null ? Number(property.bathrooms) : null,
          viewType:  property.viewType  ?? null,
          startDate,
          endDate,
          numNights,
          gapScore,
          gapNightsBefore,
          gapNightsAfter,
          gapBeforeTruncated,
          gapAfterTruncated,
          perNightFromPrice: priceFirst,
          preTaxSubtotal:    anyPrice ? +preTaxSum.toFixed(2) : null,
          // DB-authoritative totals are filled in asynchronously by
          // QuoteService.bulkTotals() — until the response lands we show
          // the tape-chart pre-tax estimate above so the card isn't blank.
          dbGrandTotal:        null,
          dbGrandTotalWithTax: null,
          dbPerNightAvg:       null,
          pricingStatus:       'pending',
          pricingError:        null,
          thumbImg:            null,
          // DB-sourced fit score filled in asynchronously by bulk-totals
          fitScore:       null,
          fitReason:      null,
          fitReasonLabel: null,
          // Post-coupon nightly rate filled in by bulk-totals response
          dbPostCouponNightlyRate: null,
        });
      }
    }

    // Sort — three tiers, consistent with applyFlexTableSort()'s 'fit' case:
    //   Tier 1: gap-fill score DESC  — best-to-sell windows first (2 > 1 > 0)
    //   Tier 2: check-in date ASC   — most urgent (nearest upcoming) dates first
    //   Tier 3: check-out date ASC  — same check-in: shorter stay before longer
    // Price is not a sort key: urgency (date proximity) is more actionable for a
    // reservationist trying to fill gaps than showing the cheapest option first.
    windows.sort((a, b) => {
      if (b.gapScore !== a.gapScore) return b.gapScore - a.gapScore;
      const dateDiff = a.startDate.getTime() - b.startDate.getTime();
      if (dateDiff !== 0) return dateDiff;
      return a.endDate.getTime() - b.endDate.getTime();
    });

    return windows;
  }

  /* ------------------------------------------------------------------ */
  /*  INTERNAL                                                            */
  /* ------------------------------------------------------------------ */

  private emptyContext(): QuoteContext {
    return {
      checkInDate: null, checkOutDate: null,
      flexMode: false, flexNumNights: null, flexRangeStart: null, flexRangeEnd: null,
      resortCode: null, resortLabel: null, resortKeywords: [],
      propertyCode: null, locationLabel: null,
      bedrooms: null,
      numAdults: 2, numChildren: 0,
      guestName: null, guestEmail: null, guestPhone: null,
      confidence: 'low', detected: [], rawSummary: ''
    };
  }

  private stripHtml(s: string): string {
    return (s || '').replace(/<style[\s\S]*?<\/style>/gi, ' ')
                    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
                    .replace(/<[^>]+>/g, ' ')
                    .replace(/&nbsp;/gi, ' ')
                    .replace(/&amp;/gi, '&')
                    .replace(/\s+/g, ' ')
                    .trim();
  }

  private titleCase(s: string): string {
    return s.replace(/\b\w/g, c => c.toUpperCase());
  }

  /**
   * Date parsing — handles:
   *   - "check in Aug 5, check out Aug 12"
   *   - "from August 1 to August 8"
   *   - "Aug 1 - Aug 8"
   *   - "8/1/2026 - 8/8/2026"
   *   - "arriving 08/01/26, departing 08/08/26"
   * Also flexible:
   *   - "any 5 nights in August"
   *   - "sometime between Aug 1 and Aug 31"
   *   - "5 days anytime in August"
   */
  private parseDates(lower: string, ctx: QuoteContext): void {

    /* ----- 1. flexible pattern: "(any/stay for/planning to stay for) N nights/days in <month>" */
    // Accepts: "any 5 nights in August", "stay for 4 nights in August",
    //          "planning to stay for 4 nights in August",
    //          "looking for 4 nights in Sept", "4 nights in Sept"
    const flexPrefixes = [
      'any', 'about', 'around', 'roughly', 'approximately',
      'stay(?:ing)?\\s*for', 'stay(?:ing)?',
      'planning\\s*to\\s*stay(?:\\s*for)?',
      'looking\\s*for', 'need(?:ing)?', 'want(?:ing)?',
      'book(?:ing)?\\s*for', 'reserve\\s*for',
      'would\\s*like\\s*to\\s*(?:stay|book|reserve)(?:\\s*for)?',
      'for'
    ].join('|');

    const flex = lower.match(
      new RegExp(
        `(?:${flexPrefixes})?\\s*(\\d+)\\s*(?:nights?|nts?|days?|nites?)\\b(?:\\s*(?:any\\s*time|sometime|somewhere|anywhere))?\\s+(?:in|during|around|within|across)\\s+([a-z]+)(?:\\s+(\\d{4}))?`
      )
    );
    if (flex) {
      const n     = +flex[1];
      const mName = flex[2];
      const year  = flex[3] ? +flex[3] : undefined;
      const range = this.monthRange(mName, year);
      if (range) {
        ctx.flexMode       = true;
        ctx.flexNumNights  = n;
        ctx.flexRangeStart = range.start;
        ctx.flexRangeEnd   = range.end;
        ctx.detected.push(`${n} nights in ${this.titleCase(mName)}${year ? ' ' + year : ''}`);
        return;
      }
    }

    /* ----- 1b. "N nights in <month>" followed anywhere in the sentence */
    // Fallback when prefix/suffix phrasing is more casual.
    const loose = lower.match(
      /(\d+)\s*(?:nights?|nts?|days?)\s+.{0,40}?\b(?:in|during)\s+([a-z]+)(?:\s+(\d{4}))?/
    );
    if (loose) {
      const n     = +loose[1];
      const mName = loose[2];
      const year  = loose[3] ? +loose[3] : undefined;
      const range = this.monthRange(mName, year);
      if (range) {
        ctx.flexMode       = true;
        ctx.flexNumNights  = n;
        ctx.flexRangeStart = range.start;
        ctx.flexRangeEnd   = range.end;
        ctx.detected.push(`${n} nights in ${this.titleCase(mName)}${year ? ' ' + year : ''}`);
        return;
      }
    }

    /* ----- 1c. just "in <month>" with no N — treat as full-month browse,
       no flex (would need a stay length). Still record the month so tape
       chart search uses that range. */
    const monthOnly = lower.match(/\b(?:in|during|visit(?:ing)?|arriv(?:e|ing))\s+([a-z]+)(?:\s+(\d{4}))?\b/);
    if (monthOnly) {
      const range = this.monthRange(monthOnly[1], monthOnly[2] ? +monthOnly[2] : undefined);
      if (range) {
        ctx.flexMode       = true;
        ctx.flexNumNights  = null;   // unknown — user/agent will pick on tape chart
        ctx.flexRangeStart = range.start;
        ctx.flexRangeEnd   = range.end;
        ctx.detected.push(`${this.titleCase(monthOnly[1])}${monthOnly[2] ? ' ' + monthOnly[2] : ''}`);
        return;
      }
    }

    /* ----- 2. "between X and Y" for flexible date range */
    const between = lower.match(/between\s+(.+?)\s+and\s+(.+?)(?:[,.]|$)/);
    if (between) {
      const start = this.tryParseDate(between[1]);
      const end   = this.tryParseDate(between[2]);
      if (start && end) {
        // "any 5 days" + "between" combo
        const nm = lower.match(/(\d+)\s*(nights?|days?)/);
        if (nm) {
          ctx.flexMode       = true;
          ctx.flexNumNights  = +nm[1];
          ctx.flexRangeStart = start;
          ctx.flexRangeEnd   = end;
          ctx.detected.push(`any ${nm[1]} nights between ${moment(start).format('MMM D')} and ${moment(end).format('MMM D')}`);
          return;
        }
        // no N specified — treat as a hard range
        ctx.checkInDate  = start;
        ctx.checkOutDate = end;
        ctx.detected.push(`${moment(start).format('MMM D')} – ${moment(end).format('MMM D')}`);
        return;
      }
    }

    /* ----- 3. explicit check-in / check-out */
    const inOut = lower.match(
      /(?:check\s*-?\s*in|checkin|arriv(?:e|al|ing)|from)\s*:?\s*(.+?)\s*(?:(?:check\s*-?\s*out|checkout|depart(?:ing|ure)?|to|through|thru|until|-|–)\s*:?\s*)(.+?)(?:[,.]|$|\s+for\s|\s+with\s)/
    );
    if (inOut) {
      const a = this.tryParseDate(inOut[1]);
      const b = this.tryParseDate(inOut[2]);
      if (a && b && b > a) {
        ctx.checkInDate  = a;
        ctx.checkOutDate = b;
        ctx.detected.push(`${moment(a).format('MMM D')} – ${moment(b).format('MMM D')}`);
        return;
      }
    }

    /* ----- 4. generic "date - date" separated by dash */
    const dash = lower.match(
      /([a-z]+\s+\d{1,2}(?:,?\s*\d{4})?|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)\s*(?:-|–|to|thru|through)\s*([a-z]+\s+\d{1,2}(?:,?\s*\d{4})?|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)/
    );
    if (dash) {
      const a = this.tryParseDate(dash[1]);
      const b = this.tryParseDate(dash[2]);
      if (a && b && b > a) {
        ctx.checkInDate  = a;
        ctx.checkOutDate = b;
        ctx.detected.push(`${moment(a).format('MMM D')} – ${moment(b).format('MMM D')}`);
        return;
      }
    }

    /* ----- 5. single start + number of nights  ("Aug 1 for 5 nights") */
    const forNights = lower.match(
      /([a-z]+\s+\d{1,2}(?:,?\s*\d{4})?|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)\s*(?:for|,)?\s*(\d+)\s*nights?/
    );
    if (forNights) {
      const a = this.tryParseDate(forNights[1]);
      const n = +forNights[2];
      if (a && n > 0) {
        const b = new Date(a);
        b.setDate(b.getDate() + n);
        ctx.checkInDate  = a;
        ctx.checkOutDate = b;
        ctx.detected.push(`${moment(a).format('MMM D')} + ${n} nights`);
      }
    }
  }

  private tryParseDate(raw: string): Date | null {
    if (!raw) return null;
    const cleaned = raw.trim()
      .replace(/^(on|the)\s+/, '')
      .replace(/(\d+)(st|nd|rd|th)\b/gi, '$1');

    const formats = [
      'M/D/YYYY', 'M/D/YY', 'MM/DD/YYYY', 'MM/DD/YY',
      'MMM D YYYY', 'MMMM D YYYY', 'MMM D, YYYY', 'MMMM D, YYYY',
      'MMM D',      'MMMM D',
      'YYYY-MM-DD',
      'D MMM YYYY', 'D MMMM YYYY', 'D MMM', 'D MMMM'
    ];

    const m = moment(cleaned, formats, true);
    if (m.isValid()) {
      // if no year supplied, assume the next upcoming occurrence
      if (!/\d{4}/.test(cleaned)) {
        const now = moment();
        if (m.year() < now.year() || (m.year() === now.year() && m.isBefore(now, 'day'))) {
          m.year(now.year() + (m.isBefore(now, 'day') ? 1 : 0));
        }
      }
      return m.toDate();
    }
    return null;
  }

  private monthRange(name: string, year?: number): { start: Date; end: Date } | null {
    const m = moment(name, ['MMMM', 'MMM'], true);
    if (!m.isValid()) return null;
    const y = year ?? (m.month() < moment().month() ? moment().year() + 1 : moment().year());
    m.year(y);
    return { start: m.clone().startOf('month').toDate(), end: m.clone().endOf('month').toDate() };
  }

  /**
   * Does this tape-chart property belong to the parsed resort?
   * Fuzzy matches against:
   *   - addressDevelopmentCode (e.g. "HK")
   *   - addressDevelopmentName (e.g. "Honua Kai") — any keyword substring
   *   - propertyCode prefix     (e.g. "HK-801" starts with "HK")
   * If no resortCode / keywords given, returns true (no filter).
   */
  matchesResort(property: any, resortCode?: string, keywords?: string[]): boolean {
    if (!resortCode && (!keywords || keywords.length === 0)) return true;
    const devCode = (property?.addressDevelopmentCode || '').toString().toUpperCase();
    const devName = (property?.addressDevelopmentName || '').toString().toLowerCase();
    const pcode   = (property?.propertyCode            || '').toString().toUpperCase();
    if (resortCode) {
      const rc = resortCode.toUpperCase();
      if (devCode === rc || devCode.startsWith(rc)) return true;
      if (pcode.startsWith(rc)) return true;
    }
    if (keywords && keywords.length) {
      for (const k of keywords) {
        if (k && devName.includes(k.toLowerCase())) return true;
      }
    }
    return false;
  }

  /**
   * Client-side filter that trims tapeChartsByProperty rows down to those
   * matching the parsed context (resort + bedroom count). Returns the
   * filtered array; callers decide whether to replace or just count.
   */
  filterPropertiesByContext(tapeChartsByProperty: any[], ctx: QuoteContext): any[] {
    if (!tapeChartsByProperty) return [];
    const wantResort = !!(ctx.resortCode || (ctx.resortKeywords && ctx.resortKeywords.length));
    const wantBeds   = ctx.bedrooms != null;
    if (!wantResort && !wantBeds) return tapeChartsByProperty;

    return tapeChartsByProperty.filter(p => {
      if (wantResort && !this.matchesResort(p, ctx.resortCode ?? undefined, ctx.resortKeywords)) {
        return false;
      }
      if (wantBeds) {
        const b = Number(p.bedrooms);
        if (!isNaN(b) && b !== ctx.bedrooms) return false;
      }
      return true;
    });
  }

  private buildSummary(ctx: QuoteContext): string {
    const parts: string[] = [];
    if (ctx.flexMode && ctx.flexNumNights) {
      parts.push(`${ctx.flexNumNights} nights, flexible ${moment(ctx.flexRangeStart).format('MMM D')}–${moment(ctx.flexRangeEnd).format('MMM D')}`);
    } else if (ctx.flexMode) {
      parts.push(`${moment(ctx.flexRangeStart).format('MMM D')}–${moment(ctx.flexRangeEnd).format('MMM D')} (flex)`);
    } else if (ctx.checkInDate && ctx.checkOutDate) {
      parts.push(`${moment(ctx.checkInDate).format('MMM D')}–${moment(ctx.checkOutDate).format('MMM D')}`);
    }
    if (ctx.resortLabel)    parts.push(ctx.resortLabel);
    else if (ctx.locationLabel) parts.push(ctx.locationLabel);
    if (ctx.propertyCode)   parts.push(ctx.propertyCode);
    if (ctx.bedrooms)       parts.push(`${ctx.bedrooms}BR`);
    const pax = `${ctx.numAdults}A${ctx.numChildren ? ' + ' + ctx.numChildren + 'C' : ''}`;
    parts.push(pax);
    return parts.join(' · ');
  }
}
